import { useState, useMemo, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { LayoutGrid, List, Plus, Receipt, Send } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { DataTable } from '~/components/ui/data-table';
import { Switch } from '~/components/ui/switch';
import { Label } from '~/components/ui/label';
import { Card, CardContent } from '~/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import type { AppLayoutContext } from '~/types';
import { decodeTableFilters, encodeTableFilters, getFacetColumnIds } from '~/utils/tableFilters';
import { useEnrollmentsQuery } from './useEnrollmentsQuery';
import { useDeleteStudentEnrollment } from './useDeleteStudentEnrollment';
import { useDeleteFamilyEnrollment } from './useDeleteFamilyEnrollment';
import { useQueueFamilyForInvite } from './useQueueFamilyForInvite';
import { useUnqueueFamily } from './useUnqueueFamily';
import { useSendFamilyInvites } from './useSendFamilyInvites';
import { useUpdateSchoolYearEnrollmentWindows } from './useUpdateSchoolYearEnrollmentWindows';
import { getFamilyColumns } from './familyColumns';
import { getStudentColumns } from './studentColumns';
import { getInquiryColumns } from './inquiryColumns';
import { InquiryLinkCard } from './InquiryLinkCard';
import { InquiryDetailModal } from './InquiryDetailModal';
import { useUpdateAdmissionInquiry } from './useUpdateAdmissionInquiry';
import { useAcceptAdmissionInquiry } from './useAcceptAdmissionInquiry';
import AddEnrollmentsWizard from './AddEnrollmentsWizard';
import { BulkInvoiceDialog } from './BulkInvoiceDialog';
import type { RowSelectionState } from '@tanstack/react-table';
import { BulkInviteDialog } from './BulkInviteDialog';
import { EnrollmentBoard } from './EnrollmentBoard';
import { FamilyEnrollmentDrawer } from './FamilyEnrollmentDrawer';
import { isNewStyle, isPhase } from './enrollmentPhases';
import type { FamilyEnrollmentRow, StudentEnrollmentRow, AdmissionInquiryRow } from './types';

function Enrollments() {
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const { schoolSlug, yearSlug } = useParams();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [inviteFamilyIds, setInviteFamilyIds] = useState<string[] | null>(null);
  const [familySelection, setFamilySelection] = useState<RowSelectionState>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'families');
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: string | null;
    enrollment: FamilyEnrollmentRow | StudentEnrollmentRow | null;
  }>({ open: false, type: null, enrollment: null });
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<ReactNode | null>(null);
  const [noticeTone, setNoticeTone] = useState<'success' | 'error'>('success');

  const showNotice = useCallback((message: ReactNode, tone: 'success' | 'error' = 'success') => {
    setNoticeTone(tone);
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 5000);
  }, []);

  // Board view, phase filter, and the open drawer all live in the URL so a
  // dashboard link (or a refresh) lands on the same family.
  const setParam = useCallback((key: string, value: string | null) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value === null) next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const boardView = searchParams.get('view') !== 'table';
  const familyParam = searchParams.get('family');
  const phaseParam = searchParams.get('phase');
  const phaseFilter = isPhase(phaseParam) ? phaseParam : null;

  // Find the school year by slug
  const schoolYear = roleContext?.selectedSchoolYear;
  const schoolYearId = schoolYear?.id;

  const { data, loading, error, refetch } = useEnrollmentsQuery(schoolYearId);
  const checklistEnrollmentOn = !!data?.schoolYear?.school?.checklistEnrollmentOn;

  const [deleteStudentEnrollment, { loading: deletingStudent }] = useDeleteStudentEnrollment();
  const [deleteFamilyEnrollment, { loading: deletingFamily }] = useDeleteFamilyEnrollment();
  const [queueFamilyForInvite] = useQueueFamilyForInvite();
  const [unqueueFamily] = useUnqueueFamily();
  const [sendFamilyInvites, { loading: sendingInvites }] = useSendFamilyInvites();
  const [updateEnrollmentWindows] = useUpdateSchoolYearEnrollmentWindows();

  const handleDeleteStudent = useCallback((enrollment: StudentEnrollmentRow) => {
    setDeleteDialog({ open: true, type: 'student', enrollment });
  }, []);

  const handleDeleteFamily = useCallback((enrollment: FamilyEnrollmentRow) => {
    setDeleteDialog({ open: true, type: 'family', enrollment });
  }, []);

  const handleQueue = useCallback((enrollment: FamilyEnrollmentRow) => {
    queueFamilyForInvite({
      variables: { familyEnrollmentId: enrollment.id },
      onCompleted: () => refetch(),
    });
  }, [queueFamilyForInvite, refetch]);

  const handleUnqueue = useCallback((enrollment: FamilyEnrollmentRow) => {
    unqueueFamily({
      variables: { familyEnrollmentId: enrollment.id },
      onCompleted: () => refetch(),
    });
  }, [unqueueFamily, refetch]);

  const handleSendInvite = useCallback((enrollment: FamilyEnrollmentRow) => {
    sendFamilyInvites({
      variables: { familyEnrollmentIds: [enrollment.id] },
      onCompleted: (result) => {
        refetch();
        const count = result?.sendFamilyInvites?.sentCount || 0;
        if (count > 0) {
          setSuccessMessage(`Successfully sent ${count} invite${count !== 1 ? 's' : ''}!`);
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      },
    });
  }, [sendFamilyInvites, refetch]);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog({ open: false, type: null, enrollment: null });
    setDeleteError(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleteDialog.enrollment) return;
    setDeleteError(null);
    if (deleteDialog.type === 'student') {
      deleteStudentEnrollment({
        variables: { studentEnrollmentId: deleteDialog.enrollment.id },
        onCompleted: (result) => {
          if (result.deleteStudentEnrollment?.success) {
            refetch();
            closeDeleteDialog();
            return;
          }
          setDeleteError(
            result.deleteStudentEnrollment?.userErrors?.map((item) => item.message).join(', ') ||
              'Unable to remove this student.',
          );
        },
        onError: (error) => setDeleteError(error.message || 'Unable to remove this student.'),
      });
    } else if (deleteDialog.type === 'family') {
      deleteFamilyEnrollment({
        variables: { familyEnrollmentId: deleteDialog.enrollment.id },
        onCompleted: (result) => {
          if (result.deleteFamilyEnrollment?.success) {
            refetch();
            closeDeleteDialog();
            return;
          }
          setDeleteError(
            result.deleteFamilyEnrollment?.userErrors?.map((item) => item.message).join(', ') ||
              'Unable to delete this enrollment.',
          );
        },
        onError: (error) => setDeleteError(error.message || 'Unable to delete this enrollment.'),
      });
    }
  }, [deleteDialog, deleteStudentEnrollment, deleteFamilyEnrollment, refetch, closeDeleteDialog]);

  // Memoize columns with schoolSlug and handlers
  const familyColumns = useMemo(
    () => getFamilyColumns(schoolSlug, {
      onDelete: handleDeleteFamily,
      onQueue: handleQueue,
      onUnqueue: handleUnqueue,
      onSendInvite: handleSendInvite,
      checklistEnrollmentOn,
    }),
    [schoolSlug, handleDeleteFamily, handleQueue, handleUnqueue, handleSendInvite, checklistEnrollmentOn]
  );
  const studentColumns = useMemo(
    () => getStudentColumns(schoolSlug, handleDeleteStudent),
    [schoolSlug, handleDeleteStudent]
  );

  // Keep tab in URL for shareable links
  useEffect(() => {
    const current = searchParams.get('tab') || 'families';
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Facet + search state synced to URL (per-table prefixes)
  const familyFacetColumnIds = useMemo(() => getFacetColumnIds(familyColumns), [familyColumns]);
  const studentFacetColumnIds = useMemo(() => getFacetColumnIds(studentColumns), [studentColumns]);

  const familySearchColumnId = 'familyName';
  const studentSearchColumnId = 'studentName';

  const familyFiltersFromUrl = useMemo(
    () =>
      decodeTableFilters({
        searchParams,
        prefix: 'family',
        facetColumnIds: familyFacetColumnIds,
        searchColumnId: familySearchColumnId,
      }),
    [searchParams, familyFacetColumnIds]
  );
  const studentFiltersFromUrl = useMemo(
    () =>
      decodeTableFilters({
        searchParams,
        prefix: 'student',
        facetColumnIds: studentFacetColumnIds,
        searchColumnId: studentSearchColumnId,
      }),
    [searchParams, studentFacetColumnIds]
  );

  const [familyColumnFilters, setFamilyColumnFilters] = useState(familyFiltersFromUrl);
  const [studentColumnFilters, setStudentColumnFilters] = useState(studentFiltersFromUrl);

  // When user navigates back/forward, re-hydrate from URL.
  useEffect(() => setFamilyColumnFilters(familyFiltersFromUrl), [familyFiltersFromUrl]);
  useEffect(() => setStudentColumnFilters(studentFiltersFromUrl), [studentFiltersFromUrl]);

  // When filters change, write into URL (replace to avoid huge history spam).
  useEffect(() => {
    const next = encodeTableFilters({
      searchParams,
      prefix: 'family',
      facetColumnIds: familyFacetColumnIds,
      searchColumnId: familySearchColumnId,
      filters: familyColumnFilters,
    });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyColumnFilters, familyFacetColumnIds]);

  useEffect(() => {
    const next = encodeTableFilters({
      searchParams,
      prefix: 'student',
      facetColumnIds: studentFacetColumnIds,
      searchColumnId: studentSearchColumnId,
      filters: studentColumnFilters,
    });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentColumnFilters, studentFacetColumnIds]);

  const familyEnrollments = useMemo(() => {
    if (!data?.schoolYear?.familyEnrollments) return [];
    // Sort by updatedAt descending by default
    return [...data.schoolYear.familyEnrollments].sort(
      (a, b) => new Date(a.updatedAt as string).getTime() - new Date(b.updatedAt as string).getTime()
    ).reverse();
  }, [data?.schoolYear?.familyEnrollments]);

  const selectedEnrollment = useMemo(
    () => familyEnrollments.find((e) => e.id === familyParam) ?? null,
    [familyEnrollments, familyParam]
  );

  const studentEnrollments = useMemo(() => {
    if (!data?.schoolYear?.studentEnrollments) return [];
    // Sort by updatedAt descending by default
    return [...data.schoolYear.studentEnrollments].sort(
      (a, b) => new Date(a.updatedAt as string).getTime() - new Date(b.updatedAt as string).getTime()
    ).reverse();
  }, [data?.schoolYear?.studentEnrollments]);

  // The invoice dialog picks from the same year the page is showing, in name
  // order: a list you read down to find a child, not the page's recency sort.
  const invoiceDialogFamilies = useMemo(
    () =>
      familyEnrollments
        .map((enrollment) => ({
          id: enrollment.id,
          name: enrollment.family?.name ?? 'Family',
          studentCount: enrollment.studentCount ?? enrollment.studentEnrollments?.length ?? 0,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [familyEnrollments]
  );

  const invoiceDialogStudents = useMemo(
    () =>
      studentEnrollments
        .map((enrollment) => ({
          personId: enrollment.person.id,
          name: [enrollment.person.firstName, enrollment.person.lastName].filter(Boolean).join(' '),
          familyName: enrollment.familyEnrollment?.family?.name ?? '',
          gradeLevel: enrollment.gradeLevel,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [studentEnrollments]
  );

  const admissionInquiries = useMemo(() => {
    if (!data?.schoolYear?.admissionInquiries) return [];
    return [...data.schoolYear.admissionInquiries].sort(
      (a, b) => new Date(b.createdAt as string).getTime() - new Date(a.createdAt as string).getTime()
    );
  }, [data?.schoolYear?.admissionInquiries]);

  const inquiryToken = data?.schoolYear?.inquiryToken ?? null;
  const [viewingInquiry, setViewingInquiry] = useState<AdmissionInquiryRow | null>(null);
  const [acceptingInquiry, setAcceptingInquiry] = useState<AdmissionInquiryRow | null>(null);
  const [updateInquiry] = useUpdateAdmissionInquiry();
  const [acceptInquiry] = useAcceptAdmissionInquiry();

  const handleUpdateInquiryState = useCallback(async (inquiry: AdmissionInquiryRow, newState: string) => {
    await updateInquiry({
      variables: { input: { admissionInquiryId: inquiry.id, state: newState } },
      refetchQueries: ['EnrollmentsQuery'],
    });
  }, [updateInquiry]);

  const handleAcceptInquiry = useCallback(async () => {
    if (!acceptingInquiry) return;
    await acceptInquiry({
      variables: { input: { admissionInquiryId: acceptingInquiry.id } },
      refetchQueries: ['EnrollmentsQuery'],
    });
    setAcceptingInquiry(null);
  }, [acceptInquiry, acceptingInquiry]);

  const inquiryColumns = useMemo(() => getInquiryColumns({
    schoolSlug,
    onView: (inquiry) => setViewingInquiry(inquiry),
    onUpdateState: handleUpdateInquiryState,
    onAccept: (inquiry) => setAcceptingInquiry(inquiry),
  }), [schoolSlug, handleUpdateInquiryState]);

  const gradeLevels = useMemo(() => {
    return data?.schoolYear?.school?.gradeLevelsSetting?.items || [];
  }, [data?.schoolYear?.school?.gradeLevelsSetting]);

  // Enrollment stats
  const stats = useMemo(() => {
    const oldFlow = familyEnrollments.filter((e) => !isNewStyle(e));
    const queued = oldFlow.filter((e) => e.state === 'READY_TO_INVITE').length;
    const invited = oldFlow.filter((e) => e.state === 'INVITED').length;
    const inProgress = oldFlow.filter((e) => e.state === 'IN_PROGRESS').length;
    const needsReview = oldFlow.filter((e) => e.state === 'NEEDS_REVIEW').length;
    const approved = oldFlow.filter((e) => ['APPROVED', 'PACKET_SENT', 'PACKET_VIEWED', 'PACKET_SUBMITTED', 'PACKET_COMPLETED'].includes(e.state)).length;
    return { queued, invited, inProgress, needsReview, approved };
  }, [familyEnrollments]);

  // Get queued enrollments that can be sent
  const sendableEnrollments = useMemo(() => {
    return familyEnrollments.filter((e) => !isNewStyle(e) && e.canSendInvite);
  }, [familyEnrollments]);

  const sendableReenrollments = useMemo(() => {
    return familyEnrollments.filter((e) => !isNewStyle(e) && e.canSendInvite && e.enrollmentKind === 'RE_ENROLLMENT');
  }, [familyEnrollments]);

  const sendableNewAdmissions = useMemo(() => {
    return familyEnrollments.filter((e) => !isNewStyle(e) && e.canSendInvite && e.enrollmentKind === 'NEW_ADMISSION');
  }, [familyEnrollments]);

  const handleSendAllReenrollments = useCallback(() => {
    if (sendableReenrollments.length === 0) return;
    sendFamilyInvites({
      variables: { familyEnrollmentIds: sendableReenrollments.map((e) => e.id) },
      onCompleted: (result) => {
        refetch();
        const count = result?.sendFamilyInvites?.sentCount || 0;
        if (count > 0) {
          setSuccessMessage(`Successfully sent ${count} re-enrollment invite${count !== 1 ? 's' : ''}!`);
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      },
    });
  }, [sendableReenrollments, sendFamilyInvites, refetch]);

  const handleSendAllNewAdmissions = useCallback(() => {
    if (sendableNewAdmissions.length === 0) return;
    sendFamilyInvites({
      variables: { familyEnrollmentIds: sendableNewAdmissions.map((e) => e.id) },
      onCompleted: (result) => {
        refetch();
        const count = result?.sendFamilyInvites?.sentCount || 0;
        if (count > 0) {
          setSuccessMessage(`Successfully sent ${count} admissions form link${count !== 1 ? 's' : ''}!`);
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      },
    });
  }, [sendableNewAdmissions, sendFamilyInvites, refetch]);

  const handleToggleReenrollment = useCallback((checked: boolean) => {
    updateEnrollmentWindows({
      variables: { schoolYearId: schoolYearId!, reenrollmentOpen: checked },
      onCompleted: () => refetch(),
    });
  }, [schoolYearId, updateEnrollmentWindows, refetch]);

  const handleToggleOpenEnrollment = useCallback((checked: boolean) => {
    updateEnrollmentWindows({
      variables: { schoolYearId: schoolYearId!, openEnrollmentOpen: checked },
      onCompleted: () => refetch(),
    });
  }, [schoolYearId, updateEnrollmentWindows, refetch]);

  if (!schoolYearId) {
    return (
      <div className="p-6">
        <div className="text-center text-muted-foreground">
          Please select a school year to view enrollments.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-muted rounded"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-destructive">
          Error loading enrollments: {error.message}
        </div>
      </div>
    );
  }

  const reenrollmentOpen = data?.schoolYear?.reenrollmentOpen;
  const openEnrollmentOpen = data?.schoolYear?.openEnrollmentOpen;

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Enrollments</h1>
          <p className="text-muted-foreground">
            {data?.schoolYear?.name} · {familyEnrollments.length} {familyEnrollments.length === 1 ? 'family' : 'families'} · {studentEnrollments.length} {studentEnrollments.length === 1 ? 'student' : 'students'}
          </p>
        </div>
        {/*
          * The windows gate invites, so they sit next to the pipeline they
          * gate. Side by side they overran a phone and clipped the second
          * window's own state, so on a narrow screen each window is its own
          * full-width row with the state pushed to the right where a settings
          * row normally carries it.
          */}
        <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
          {!checklistEnrollmentOn && (
          <div className="flex flex-col divide-y rounded-md border bg-background sm:h-10 sm:flex-row sm:items-center sm:gap-5 sm:divide-y-0 sm:px-3.5">
            <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 sm:px-0 sm:py-0">
              <div className="flex items-center gap-2">
                <Switch id="reenrollment" checked={!!reenrollmentOpen} onCheckedChange={handleToggleReenrollment} />
                <Label htmlFor="reenrollment" className="font-medium">Re-enrollment</Label>
              </div>
              <span className="text-sm text-muted-foreground">{reenrollmentOpen ? 'open' : 'closed'}</span>
            </div>
            <div className="hidden h-5 w-px bg-border sm:block" />
            <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 sm:px-0 sm:py-0">
              <div className="flex items-center gap-2">
                <Switch id="openEnrollment" checked={!!openEnrollmentOpen} onCheckedChange={handleToggleOpenEnrollment} />
                <Label htmlFor="openEnrollment" className="font-medium">New admissions</Label>
              </div>
              <span className="text-sm text-muted-foreground">{openEnrollmentOpen ? 'open' : 'closed'}</span>
            </div>
          </div>
          )}
          <Button variant="outline" onClick={() => setIsInvoiceDialogOpen(true)} className="w-full sm:w-auto">
            <Receipt className="mr-2 h-4 w-4" />
            Create Invoices
          </Button>
          <Button onClick={() => setIsWizardOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Add Enrollments
          </Button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div
          role="status"
          className={`mb-4 p-4 rounded-md border ${noticeTone === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}
        >
          <span className="font-medium">{successMessage}</span>
        </div>
      )}

      {/* Warning when families are queued but window is closed */}
      {!checklistEnrollmentOn && stats.queued > 0 && sendableEnrollments.length === 0 && (
        <Card className="mb-6 border-yellow-500/50 bg-yellow-50">
          <CardContent className="flex flex-col items-start justify-between gap-3 py-4 sm:flex-row sm:items-center sm:gap-4">
            <div>
              <p className="font-medium text-yellow-800">
                {stats.queued} enrollment{stats.queued !== 1 ? 's' : ''} queued but cannot be sent
              </p>
              <p className="text-sm text-yellow-700">
                {!reenrollmentOpen && 'Re-enrollment window is closed. '}
                {!openEnrollmentOpen && 'New admissions window is closed. '}
                Open the window above to send invites.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Send Re-enrollment Invites */}
      {sendableReenrollments.length > 0 && (
        <Card className="mb-6 border-primary/50 bg-primary/5">
          <CardContent className="flex flex-col items-start justify-between gap-3 py-4 sm:flex-row sm:items-center sm:gap-4">
            <div>
              <p className="font-medium">
                {sendableReenrollments.length} re-enrollment invite{sendableReenrollments.length !== 1 ? 's' : ''} ready to send
              </p>
              <p className="text-sm text-muted-foreground">
                {reenrollmentOpen ? 'Re-enrollment window is open' : 'Re-enrollment window is closed'}
              </p>
            </div>
            <Button onClick={handleSendAllReenrollments} disabled={sendingInvites} className="w-full sm:w-auto">
              <Send className="mr-2 h-4 w-4" />
              {sendingInvites ? 'Sending...' : `Send ${sendableReenrollments.length} Re-enrollment Invite${sendableReenrollments.length !== 1 ? 's' : ''}`}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Send Admissions Form Links */}
      {sendableNewAdmissions.length > 0 && (
        <Card className="mb-6 border-blue-500/50 bg-blue-50">
          <CardContent className="flex flex-col items-start justify-between gap-3 py-4 sm:flex-row sm:items-center sm:gap-4">
            <div>
              <p className="font-medium">
                {sendableNewAdmissions.length} admissions form link{sendableNewAdmissions.length !== 1 ? 's' : ''} ready to send
              </p>
              <p className="text-sm text-muted-foreground">
                {openEnrollmentOpen ? 'New admissions window is open' : 'New admissions window is closed'}
              </p>
            </div>
            <Button onClick={handleSendAllNewAdmissions} disabled={sendingInvites} className="w-full sm:w-auto">
              <Send className="mr-2 h-4 w-4" />
              {sendingInvites ? 'Sending...' : `Send ${sendableNewAdmissions.length} Admissions Form Link${sendableNewAdmissions.length !== 1 ? 's' : ''}`}
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="families">
            Families ({familyEnrollments.length})
          </TabsTrigger>
          <TabsTrigger value="students">
            Students ({studentEnrollments.length})
          </TabsTrigger>
          <TabsTrigger value="inquiries">
            Inquiries ({admissionInquiries.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="families" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Families view">
              <Button
                variant={boardView ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 gap-1.5 px-2.5"
                aria-pressed={boardView}
                onClick={() => setParam('view', null)}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Board
              </Button>
              <Button
                variant={boardView ? 'ghost' : 'secondary'}
                size="sm"
                className="h-7 gap-1.5 px-2.5"
                aria-pressed={!boardView}
                onClick={() => setParam('view', 'table')}
              >
                <List className="h-3.5 w-3.5" />
                Table
              </Button>
            </div>
          </div>
          {boardView ? (
            <EnrollmentBoard
              enrollments={familyEnrollments}
              selectedId={familyParam}
              onSelect={(enrollment) => setParam('family', enrollment.id)}
              phaseFilter={phaseFilter}
              onClearPhaseFilter={() => setParam('phase', null)}
              reenrollmentOpen={reenrollmentOpen}
              openEnrollmentOpen={openEnrollmentOpen}
              checklistEnrollmentOn={checklistEnrollmentOn}
            />
          ) : (
            <DataTable
              columns={familyColumns}
              data={familyEnrollments}
              searchColumn="familyName"
              searchPlaceholder="Filter families..."
              defaultSorting={[{ id: 'familyName', desc: false }]}
              stateKey={`enrollments-families-${schoolYearId}`}
              columnFilters={familyColumnFilters}
              onColumnFiltersChange={setFamilyColumnFilters}
              enableRowSelection
              rowSelection={familySelection}
              onRowSelectionChange={setFamilySelection}
              bulkActions={
                checklistEnrollmentOn
                  ? [{ label: 'Invite to enroll', onClick: (rows) => setInviteFamilyIds(rows.map((row) => row.id)) }]
                  : []
              }
            />
          )}
        </TabsContent>

        <TabsContent value="students" className="mt-4">
          <DataTable
            columns={studentColumns}
            data={studentEnrollments}
            searchColumn="studentName"
            searchPlaceholder="Filter students..."
            defaultSorting={[{ id: 'studentName', desc: false }]}
            stateKey={`enrollments-students-${schoolYearId}`}
            columnFilters={studentColumnFilters}
            onColumnFiltersChange={setStudentColumnFilters}
            enableRowSelection
          />
        </TabsContent>

        <TabsContent value="inquiries" className="mt-4">
          <InquiryLinkCard
            inquiryToken={inquiryToken}
            schoolYearId={schoolYearId}
            onGenerated={() => refetch()}
          />
          <DataTable
            columns={inquiryColumns}
            data={admissionInquiries}
            searchColumn="name"
            searchPlaceholder="Filter inquiries..."
            defaultSorting={[{ id: 'name', desc: false }]}
            stateKey={`enrollments-inquiries-${schoolYearId}`}
          />
        </TabsContent>
      </Tabs>

      {selectedEnrollment && data?.schoolYear && (
        <FamilyEnrollmentDrawer
          enrollment={selectedEnrollment}
          schoolSlug={schoolSlug ?? school?.slug ?? ''}
          checklistEnrollmentOn={checklistEnrollmentOn}
          schoolYear={{
            id: data.schoolYear.id,
            name: data.schoolYear.name,
            reenrollmentOpen: data.schoolYear.reenrollmentOpen,
            openEnrollmentOpen: data.schoolYear.openEnrollmentOpen,
          }}
          onClose={() => setParam('family', null)}
          onChanged={() => refetch()}
          onNotice={showNotice}
        />
      )}

      <AddEnrollmentsWizard
        open={isWizardOpen}
        onOpenChange={setIsWizardOpen}
        schoolId={school?.id}
        schoolYearId={schoolYearId}
        schoolYearName={data?.schoolYear?.name}
        gradeLevels={gradeLevels}
        checklistEnrollmentOn={checklistEnrollmentOn}
      />

      {school?.id && (
        <BulkInviteDialog
          open={inviteFamilyIds !== null}
          onOpenChange={(open) => { if (!open) setInviteFamilyIds(null); }}
          schoolId={school.id}
          familyEnrollmentIds={inviteFamilyIds ?? []}
          onCompleted={() => { setFamilySelection({}); void refetch(); }}
        />
      )}

      <BulkInvoiceDialog
        open={isInvoiceDialogOpen}
        onOpenChange={setIsInvoiceDialogOpen}
        schoolYearId={schoolYearId}
        families={invoiceDialogFamilies}
        students={invoiceDialogStudents}
        invoicesPath={`/app/${schoolSlug}/years/${yearSlug}/invoices`}
        onCompleted={(message) => {
          showNotice(message);
          void refetch();
        }}
      />

      {/* Inquiry Detail Modal */}
      <InquiryDetailModal
        inquiry={viewingInquiry}
        open={viewingInquiry !== null}
        onOpenChange={(open) => { if (!open) setViewingInquiry(null); }}
      />

      {/* Accept Inquiry Confirmation */}
      <AlertDialog open={acceptingInquiry !== null} onOpenChange={(open) => { if (!open) setAcceptingInquiry(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Accept for Admission</AlertDialogTitle>
            <AlertDialogDescription>
              {checklistEnrollmentOn ? (
                <>
                  Accepting adds the family and children to {schoolYear?.name} as No answer yet. After the interview, set the
                  children to Coming and invite the family to enroll.
                </>
              ) : (
                <>
                  This will create an enrollment record for the{' '}
                  <strong>{acceptingInquiry?.parentLastName} Family</strong> to begin the
                  process of filling out necessary information to be sent an enrollment packet.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAcceptInquiry}>
              Accept & Create Enrollment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteDialog.type === 'student' ? 'Delete Student Enrollment' : 'Delete Family Enrollment'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.type === 'student' ? (
                <>
                  Are you sure you want to remove{' '}
                  <strong>{(deleteDialog.enrollment as StudentEnrollmentRow | null)?.person?.firstName} {(deleteDialog.enrollment as StudentEnrollmentRow | null)?.person?.lastName}</strong>{' '}
                  from this year&apos;s enrollment? This action cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to delete the enrollment for{' '}
                  <strong>{(deleteDialog.enrollment as FamilyEnrollmentRow | null)?.family?.name}</strong>?
                  This will also remove all {(deleteDialog.enrollment as FamilyEnrollmentRow | null)?.studentCount || 0} student enrollments. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
            {deleteError && (
              <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {deleteError}
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                confirmDelete();
              }}
              disabled={deletingStudent || deletingFamily}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(deletingStudent || deletingFamily) ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Enrollments;
